import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../utils/api'
import { auth } from '../lib/firebase'

export default function Register() {
  const navigate = useNavigate()
  const { register, setUser } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tier, setTier] = useState('free')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  
  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    
    try {
      if (!import.meta.env.VITE_FIREBASE_API_KEY) {
        const mockToken = 'mock-token-' + Date.now()
        localStorage.setItem('beat-auth-token', mockToken)
        
        const response = await api.auth.register(mockToken, tier)
        setUser({ uid: response.uid, email: response.email })
        navigate('/dashboard')
        return
      }
      
      const { createUserWithEmailAndPassword } = await import('firebase/auth')
      const result = await createUserWithEmailAndPassword(auth, email, password)
      const token = await result.user.getIdToken()
      
      register(token)
      
      const response = await api.auth.register(token, tier)
      setUser({ uid: response.uid, email: response.email })
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }
  
  const handleGoogleRegister = async () => {
    setError('')
    setLoading(true)
    
    try {
      const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth')
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      const token = await result.user.getIdToken()
      
      register(token)
      
      const response = await api.auth.register(token, tier)
      setUser({ uid: response.uid, email: response.email })
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }
  
  const tiers = [
    { id: 'free', name: 'Free', price: '$0/mo', description: '5 SMS, 10 emails/month' },
    { id: 'email', name: 'Email Only', price: '$2.99/mo', description: 'Unlimited emails with attachments' },
    { id: 'starter', name: 'Starter SMS', price: '$4.99/mo', description: '100 SMS, unlimited emails' },
  ]
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Create your account</h2>
          <p className="mt-2 text-gray-600">
            Already have an account? <a href="/login" className="text-blue-600 hover:underline">Sign in</a>
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Choose your plan</label>
            <div className="space-y-2">
              {tiers.map((t) => (
                <label
                  key={t.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                    tier === t.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="tier"
                    value={t.id}
                    checked={tier === t.id}
                    onChange={(e) => setTier(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <span className="font-medium">{t.name}</span>
                    <span className="ml-2 text-gray-600">{t.price}</span>
                    <p className="text-sm text-gray-500">{t.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleEmailRegister}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input mt-1"
                placeholder="you@example.com"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input mt-1"
                placeholder="Minimum 6 characters"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full py-3"
          >
            {loading ? 'Creating account...' : 'Sign up with Email'}
          </button>
        </form>
        
        <div className="text-center">
          <span className="text-gray-500">or</span>
        </div>
        
        <button
          type="button"
          onClick={handleGoogleRegister}
          disabled={loading}
          className="btn btn-secondary w-full py-3"
        >
          Continue with Google
        </button>
      </div>
    </div>
  )
}